/**
 * Marketing Effectiveness Section for Admin Control Panel
 * 
 * Phase 2: Key conversion rates and engagement depth metrics.
 */
import React from "react";
import { 
  TrendingUp, TrendingDown, Minus, 
  UserCheck, Play, Clock, BarChart3, AlertTriangle
} from "lucide-react";
import SummaryCard from "./SummaryCard";

const MarketingEffectivenessSection = ({ data, onCardClick, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-slate-900/60 border border-slate-700 rounded-xl p-5 animate-pulse">
            <div className="h-4 bg-slate-800 rounded w-24 mb-3"></div>
            <div className="h-8 bg-slate-800 rounded w-16 mb-2"></div>
            <div className="h-3 bg-slate-800 rounded w-32"></div>
          </div>
        ))}
      </div>
    );
  }

  const effectiveness = data?.effectiveness || {};

  // Helper to calculate trend
  const getTrend = (value, isPercentChange = false) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (value > 0) {
      return { direction: 'up', value: Math.abs(value).toFixed(1) };
    } else if (value < 0) {
      return { direction: 'down', value: Math.abs(value).toFixed(1) };
    }
    return { direction: 'neutral', value: 0 };
  };

  // Format time to human readable
  const formatTime = (minutes) => {
    if (minutes === null || minutes === undefined) {
      return "No data";
    }
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    if (minutes < 1440) { // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    const days = (minutes / 1440).toFixed(1);
    return `${days} days`;
  };

  const cards = [
    {
      key: "visitor_registration",
      title: "Visitor → Registered",
      value: effectiveness.visitor_to_registration_rate !== null 
        ? `${effectiveness.visitor_to_registration_rate}%` 
        : "—",
      rawValue: effectiveness.visitor_to_registration_rate,
      icon: UserCheck,
      iconBgColor: "bg-blue-900/50",
      iconColor: "text-blue-400",
      trend: getTrend(effectiveness.visitor_to_registration_trend),
      trendLabel: "vs prev period",
      description: "Of all visitors who created an account",
      benchmark: "Industry avg: 2-5%",
      clickable: false
    },
    {
      key: "registration_engagement",
      title: "Registered → Engaged",
      value: effectiveness.registration_to_engagement_rate !== null 
        ? `${effectiveness.registration_to_engagement_rate}%` 
        : "—",
      rawValue: effectiveness.registration_to_engagement_rate,
      icon: Play,
      iconBgColor: "bg-purple-900/50",
      iconColor: "text-purple-400",
      trend: getTrend(effectiveness.registration_to_engagement_trend),
      trendLabel: "vs prev period",
      description: "Of registered users who played content",
      clickable: false
    },
    {
      key: "time_to_listen",
      title: "Time to First Listen",
      value: formatTime(effectiveness.avg_time_to_first_listen_minutes),
      rawValue: effectiveness.avg_time_to_first_listen_minutes,
      icon: Clock,
      iconBgColor: "bg-emerald-900/50",
      iconColor: "text-emerald-400",
      description: "Avg. time from signup to first play",
      clickable: false,
      noData: effectiveness.avg_time_to_first_listen_minutes === null
    },
    {
      key: "sessions_per_user",
      title: "Sessions per User",
      value: effectiveness.sessions_per_active_user !== null 
        ? effectiveness.sessions_per_active_user.toFixed(1) 
        : "—",
      rawValue: effectiveness.sessions_per_active_user,
      icon: BarChart3,
      iconBgColor: "bg-cyan-900/50",
      iconColor: "text-cyan-400",
      trend: getTrend(effectiveness.sessions_per_active_user_trend),
      trendLabel: "vs prev period",
      description: "Average engagement depth",
      clickable: false
    },
    {
      key: "never_listened",
      title: "Never Listened",
      value: effectiveness.registered_never_listened || 0,
      rawValue: effectiveness.registered_never_listened,
      icon: AlertTriangle,
      iconBgColor: "bg-amber-900/50",
      iconColor: "text-amber-400",
      description: effectiveness.registered_never_listened_percent !== null
        ? `${effectiveness.registered_never_listened_percent}% of registrations`
        : "Signed up but never engaged",
      clickable: true,
      isWarning: effectiveness.registered_never_listened > 0
    }
  ];

  return (
    <div data-testid="marketing-effectiveness-section">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
          Marketing Effectiveness
        </span>
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((card) => {
          const { key, ...cardProps } = card;
          return (
            <EffectivenessCard
              key={key}
              {...cardProps}
              onClick={card.clickable ? () => onCardClick(key, card.title) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

// Individual effectiveness card component
const EffectivenessCard = ({
  title,
  value,
  rawValue,
  icon: Icon,
  iconBgColor,
  iconColor,
  trend,
  trendLabel,
  description,
  benchmark,
  clickable,
  onClick,
  noData,
  isWarning
}) => {
  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-3 h-3" />;
      case 'down':
        return <TrendingDown className="w-3 h-3" />;
      default:
        return <Minus className="w-3 h-3" />;
    }
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'up':
        return 'text-emerald-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <div
      className={`
        bg-slate-900/60 border rounded-xl p-5 transition-all
        ${isWarning ? 'border-amber-700/50 bg-amber-900/10' : 'border-slate-700'}
        ${clickable ? 'cursor-pointer hover:border-slate-600 hover:bg-slate-800/60 group' : ''}
        ${noData ? 'opacity-60' : ''}
      `}
      onClick={onClick}
      data-testid={`effectiveness-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
          {title}
        </p>
        <div className={`p-2 rounded-lg ${iconBgColor}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
      </div>

      <div className={`text-2xl font-bold mb-2 ${noData ? 'text-slate-500' : 'text-white'}`}>
        {value}
      </div>

      {/* Trend indicator */}
      {trend && !noData && (
        <div className={`flex items-center gap-1 mb-2 ${getTrendColor(trend.direction)}`}>
          {getTrendIcon(trend.direction)}
          <span className="text-sm font-medium">{trend.value}%</span>
          {trendLabel && (
            <span className="text-xs text-slate-500 ml-1">{trendLabel}</span>
          )}
        </div>
      )}

      {/* Description */}
      <p className="text-xs text-slate-500">{description}</p>

      {/* Benchmark */}
      {benchmark && !noData && (
        <p className="text-xs text-slate-600 mt-1 italic">{benchmark}</p>
      )}

      {/* Click hint for clickable cards */}
      {clickable && (
        <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-800 opacity-0 group-hover:opacity-100 transition-opacity">
          Click to view re-engagement targets
        </p>
      )}
    </div>
  );
};

export default MarketingEffectivenessSection;
